import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { CheckCircle2, XCircle, Clock, Loader2, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001/api';

const AdminApprovalsPage = () => {
  const [pending, setPending] = useState([]);
  const [allIntegrations, setAllIntegrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      // Load pending approvals
      const pendingRes = await axios.get(`${API_BASE_URL}/admin/api-approvals/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPending(pendingRes.data.integrations || []);

      // Load all integrations
      const allRes = await axios.get(`${API_BASE_URL}/admin/api-approvals/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllIntegrations(allRes.data.integrations || []);

      // Load stats
      const statsRes = await axios.get(`${API_BASE_URL}/admin/api-approvals/stats/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async (integrationId) => {
    try {
      setTesting(true);
      const token = localStorage.getItem('authToken');

      const response = await axios.post(
        `${API_BASE_URL}/admin/api-approvals/${integrationId}/test`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('Connection test successful!');
      } else {
        toast.error('Connection test failed');
      }
      loadData();
    } catch (error) {
      console.error('Error testing:', error);
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleApprove = async (integrationId, decision) => {
    try {
      setProcessing(true);
      const token = localStorage.getItem('authToken');

      await axios.post(
        `${API_BASE_URL}/admin/api-approvals/${integrationId}/approve`,
        {
          decision: decision,
          notes: notes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(`Integration ${decision}d successfully!`);
      setSelectedIntegration(null);
      setNotes('');
      loadData();
    } catch (error) {
      console.error('Error processing approval:', error);
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    } finally {
      setProcessing(false);
    }
  };

  const handleRevoke = async (integrationId) => {
    if (!window.confirm('Are you sure you want to revoke this integration?')) return;

    try {
      const token = localStorage.getItem('authToken');
      await axios.post(
        `${API_BASE_URL}/admin/api-approvals/${integrationId}/revoke`,
        { reason: 'Revoked by admin' },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Integration revoked');
      loadData();
    } catch (error) {
      console.error('Error revoking:', error);
      toast.error('Failed to revoke integration');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      active: { icon: CheckCircle2, color: 'bg-green-100 text-green-800', text: 'Active' },
      failed: { icon: XCircle, color: 'bg-red-100 text-red-800', text: 'Failed' },
      rejected: { icon: XCircle, color: 'bg-gray-100 text-gray-800', text: 'Rejected' },
      revoked: { icon: XCircle, color: 'bg-red-100 text-red-800', text: 'Revoked' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </Badge>
    );
  };

  const IntegrationCard = ({ integration, showActions = false }) => (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold capitalize">{integration.provider?.replace(/_/g, ' ')}</h3>
          <p className="text-sm text-gray-500">
            Submitted {new Date(integration.submitted_at).toLocaleString()}
          </p>
          {integration.approved_at && (
            <p className="text-sm text-gray-500">
              Approved {new Date(integration.approved_at).toLocaleString()}
            </p>
          )}
        </div>
        {getStatusBadge(integration.status)}
      </div>

      {integration.description && (
        <p className="text-sm text-gray-600">{integration.description}</p>
      )}

      {integration.test_result && (
        <div className={`text-xs p-2 rounded ${integration.test_result.success ? 'bg-green-50' : 'bg-red-50'}`}>
          <p className={integration.test_result.success ? 'text-green-700' : 'text-red-700'}>
            {integration.test_result.message}
          </p>
          {integration.last_tested && (
            <p className="text-gray-500 mt-1">
              Last tested: {new Date(integration.last_tested).toLocaleString()}
            </p>
          )}
        </div>
      )}

      {showActions && (
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleTest(integration.id)}
            disabled={testing}
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test Connection'}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              setSelectedIntegration(integration);
              setNotes('');
            }}
          >
            Review
          </Button>
        </div>
      )}

      {!showActions && integration.status === 'active' && (
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleTest(integration.id)}
            disabled={testing}
          >
            Test
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => handleRevoke(integration.id)}
          >
            Revoke
          </Button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-8 flex items-center gap-3">
        <Shield className="w-8 h-8" />
        <div>
          <h1 className="text-3xl font-bold">Admin Approvals</h1>
          <p className="text-gray-600">Review and manage API integration requests</p>
        </div>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm text-gray-600">Total</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-yellow-600">{stats.by_status?.pending || 0}</div>
              <div className="text-sm text-gray-600">Pending</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-green-600">{stats.by_status?.active || 0}</div>
              <div className="text-sm text-gray-600">Active</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-red-600">{stats.by_status?.failed || 0}</div>
              <div className="text-sm text-gray-600">Failed</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-orange-600">{stats.requiring_attention}</div>
              <div className="text-sm text-gray-600">Need Attention</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending">
            Pending ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="all">
            All Integrations ({allIntegrations.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Pending Approvals</CardTitle>
              <CardDescription>Review and approve integration requests</CardDescription>
            </CardHeader>
            <CardContent>
              {pending.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No pending approvals
                </div>
              ) : (
                <div className="space-y-4">
                  {pending.map((integration) => (
                    <IntegrationCard key={integration.id} integration={integration} showActions={true} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="all" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>All Integrations</CardTitle>
              <CardDescription>View all integration requests</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {allIntegrations.map((integration) => (
                  <IntegrationCard key={integration.id} integration={integration} />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      {selectedIntegration && (
        <Dialog open={!!selectedIntegration} onOpenChange={() => setSelectedIntegration(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Review Integration</DialogTitle>
              <DialogDescription>
                Provider: {selectedIntegration.provider?.replace(/_/g, ' ')}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div>
                <Label>Submitted</Label>
                <p className="text-sm text-gray-600">
                  {new Date(selectedIntegration.submitted_at).toLocaleString()}
                </p>
              </div>

              {selectedIntegration.description && (
                <div>
                  <Label>Description</Label>
                  <p className="text-sm text-gray-600">{selectedIntegration.description}</p>
                </div>
              )}

              <div>
                <Label htmlFor="notes">Admin Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Add notes about this approval decision..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  variant="default"
                  className="flex-1"
                  onClick={() => handleApprove(selectedIntegration.id, 'approve')}
                  disabled={processing}
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Approve
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => handleApprove(selectedIntegration.id, 'reject')}
                  disabled={processing}
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                  Reject
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AdminApprovalsPage;
